import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { validatePassword } from '../utils/validation';
import PasswordStrengthIndicator from '../components/register/PasswordStrengthIndicator';
import { AlertCircle, Loader2, ArrowLeft, Lock, Eye, EyeOff, CheckCircle, KeyRound, ChevronRight } from 'lucide-react';

const gridOverlayStyle = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
};

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { confirmPassword } = useAuth();
  const email = (location.state as any)?.email || '';

  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => navigate('/login'), 3000);
      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  const Logo = () => (
    <div className="flex justify-center">
      {logoError ? (
        <div className="inline-flex items-center justify-center w-[60px] h-[60px] bg-niwt-primary rounded-2xl">
          <span className="text-white text-2xl font-bold font-libre">N</span>
        </div>
      ) : (
        <img
          src="/images/niwt-logo.png"
          alt="Niwt"
          width={60}
          height={60}
          onError={() => setLogoError(true)}
        />
      )}
    </div>
  );

  // No email in router state — user arrived directly
  if (!email) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2D1B69] to-[#1A0F3D] flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10" style={gridOverlayStyle} />
        <div className="sm:mx-auto sm:w-full sm:max-w-md z-10">
          <Logo />
          <h1 className="mt-6 text-center text-3xl font-libre font-bold text-white">Reset Your Password</h1>
        </div>
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md z-10">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">No Reset Code Requested</h2>
            <p className="text-gray-600 text-sm mb-6">
              You need to request a verification code first.
            </p>
            <Link
              to="/forgot-password"
              className="inline-flex items-center justify-center w-full bg-niwt-primary text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#6B51A0] transition-colors"
            >
              Request Reset Code
              <ChevronRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
        <div className="mt-8 text-center z-10">
          <div className="flex items-center justify-center gap-4 text-sm">
            <a href="https://www.niwt.com" className="text-white/80 hover:text-white hover:underline transition-colors">niwt.com</a>
            <span className="text-white/40">&bull;</span>
            <a href="https://www.niwt.com/privacy" className="text-white/80 hover:text-white hover:underline transition-colors">Privacy</a>
            <span className="text-white/40">&bull;</span>
            <a href="https://www.niwt.com/terms" className="text-white/80 hover:text-white hover:underline transition-colors">Terms</a>
          </div>
          <div className="mt-4 flex items-center justify-center gap-3">
            <span className="px-3 py-1 bg-white/10 border border-white/20 rounded text-xs font-semibold text-white/60 tracking-wider">SOC2</span>
            <span className="px-3 py-1 bg-white/10 border border-white/20 rounded text-xs font-semibold text-white/60 tracking-wider">ENCRYPTED</span>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2D1B69] to-[#1A0F3D] flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10" style={gridOverlayStyle} />
        <div className="sm:mx-auto sm:w-full sm:max-w-md z-10">
          <Logo />
        </div>
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md z-10">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Password Reset Successful</h2>
            <p className="text-gray-600 mb-6">
              Your password has been updated. Redirecting to login...
            </p>
            <div className="flex justify-center mb-4">
              <Loader2 className="w-6 h-6 text-niwt-primary animate-spin" />
            </div>
            <Link to="/login" className="text-sm text-niwt-primary hover:text-niwt-secondary">
              Click here if not redirected
            </Link>
          </div>
        </div>
        <div className="mt-8 text-center z-10">
          <div className="flex items-center justify-center gap-4 text-sm">
            <a href="https://www.niwt.com" className="text-white/80 hover:text-white hover:underline transition-colors">niwt.com</a>
            <span className="text-white/40">&bull;</span>
            <a href="https://www.niwt.com/privacy" className="text-white/80 hover:text-white hover:underline transition-colors">Privacy</a>
            <span className="text-white/40">&bull;</span>
            <a href="https://www.niwt.com/terms" className="text-white/80 hover:text-white hover:underline transition-colors">Terms</a>
          </div>
          <div className="mt-4 flex items-center justify-center gap-3">
            <span className="px-3 py-1 bg-white/10 border border-white/20 rounded text-xs font-semibold text-white/60 tracking-wider">SOC2</span>
            <span className="px-3 py-1 bg-white/10 border border-white/20 rounded text-xs font-semibold text-white/60 tracking-wider">ENCRYPTED</span>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!code || code.length !== 6) {
      setError('Please enter the 6-digit verification code.');
      return;
    }

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors[0]);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      await confirmPassword(email, code, newPassword);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2D1B69] to-[#1A0F3D] flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Grid overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-10" style={gridOverlayStyle} />

      {/* Logo and Title */}
      <div className="sm:mx-auto sm:w-full sm:max-w-md z-10">
        <Logo />
        <h1 className="mt-6 text-center text-3xl font-libre font-bold text-white">Reset Your Password</h1>
        <p className="mt-2 text-center text-sm text-white/70">Enter the code sent to {email}</p>
      </div>

      {/* Form */}
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md z-10">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <Link
            to="/forgot-password"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Link>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Verification Code */}
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <KeyRound className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  value={code}
                  onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                  required
                  maxLength={6}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:border-transparent hover:border-gray-400 transition-all tracking-widest text-lg"
                  placeholder="000000"
                  autoComplete="one-time-code"
                  autoFocus
                  disabled={loading}
                />
              </div>
            </div>

            {/* New Password */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                  required
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:border-transparent hover:border-gray-400 transition-all"
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <PasswordStrengthIndicator password={newPassword} email={email} />
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmNewPassword}
                  onChange={(e) => { setConfirmNewPassword(e.target.value); setError(''); }}
                  required
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:border-transparent hover:border-gray-400 transition-all"
                  placeholder="Re-enter new password"
                  autoComplete="new-password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !code || !newPassword || !confirmNewPassword}
              className="w-full bg-niwt-primary text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#6B51A0] focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Resetting Password...
                </>
              ) : (
                <>
                  Reset Password
                  <ChevronRight className="w-5 h-5 ml-2" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/forgot-password" className="text-sm text-niwt-primary hover:text-niwt-secondary">
              Didn't receive a code? Send again
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center z-10">
        <div className="flex items-center justify-center gap-4 text-sm">
          <a href="https://www.niwt.com" className="text-white/80 hover:text-white hover:underline transition-colors">niwt.com</a>
          <span className="text-white/40">&bull;</span>
          <a href="https://www.niwt.com/privacy" className="text-white/80 hover:text-white hover:underline transition-colors">Privacy</a>
          <span className="text-white/40">&bull;</span>
          <a href="https://www.niwt.com/terms" className="text-white/80 hover:text-white hover:underline transition-colors">Terms</a>
        </div>
        <div className="mt-4 flex items-center justify-center gap-3">
          <span className="px-3 py-1 bg-white/10 border border-white/20 rounded text-xs font-semibold text-white/60 tracking-wider">SOC2</span>
          <span className="px-3 py-1 bg-white/10 border border-white/20 rounded text-xs font-semibold text-white/60 tracking-wider">ENCRYPTED</span>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
